import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, MapPin, Send, Check, X, ArrowLeft, Navigation, Play, Eye, Car, CheckCircle, Map, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import GroupTripPanel from "../components/carpool/GroupTripPanel";

const TRIP_ACTIVE_STATUSES = ["accepted", "started", "arrived", "picked_up"];

const CarpoolPage = () => {
  const [matches, setMatches] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], sent: [] });
  const [loading, setLoading] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showMapDialog, setShowMapDialog] = useState(false);
  const [selectedRouteRequest, setSelectedRouteRequest] = useState(null);
  const [showGroupTrip, setShowGroupTrip] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = getLocalDateString(new Date());

  const [requestData, setRequestData] = useState({
    date: today,
    message: "",
    timeShift: "",
  });
  const [timeShifts, setTimeShifts] = useState([]);
  const navigate = useNavigate();

  const dateStr = today;
  useEffect(() => {
    fetchTimeShifts();
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [requestData.timeShift]);

  const fetchTimeShifts = async () => {
    try {
      const res = await axios.get(`${API}/timeslots`);
      setTimeShifts(res.data);
    } catch (error) {
      toast.error("Failed to load time shifts");
    }
  };

  const fetchData = async () => {
    try {
      const requestsRes = await axios.get(`${API}/carpool/requests`);
      setRequests(requestsRes.data);

      if (requestData.timeShift) {
        const matchesRes = await axios.get(`${API}/carpool/matches?date=${dateStr}&time_window=${requestData.timeShift}`);
        setMatches(matchesRes.data);
      } else {
        setMatches([]);
      }
    } catch (error) {
      toast.error("Failed to load carpool data");
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (userId) => {
    if ( !requestData.message) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      await axios.post(`${API}/carpool/request`, {
        recipient_id: userId,
        date: requestData.date,
        message: requestData.message,
      });
      toast.success("Carpool request sent!");
      setShowRequestDialog(false);
      setRequestData({ date: "", message: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to send request");
    }
  };

  const handleUpdateRequest = async (requestId, status) => {
    try {
      await axios.put(`${API}/carpool/requests/${requestId}?status=${status}`);
      toast.success(`Request ${status}!`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update request");
    }
  };

  const acceptedIncoming = (requests.incoming ?? []).filter((r) =>
    TRIP_ACTIVE_STATUSES.includes(r.status)
  );
  const groupTripDate = acceptedIncoming[0]?.date || today;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="carpool-page">
      <div className="pt-12 pb-12 px-6 max-w-7xl mx-auto">
        <Button
          data-testid="back-to-dashboard"
          onClick={() => navigate("/dashboard")}
          variant="ghost"
          className="mb-6 text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Dashboard
        </Button>

        <div className="fade-in">
          <div className="mb-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">
              Carpool Network
            </h1>
            <p className="text-xl text-gray-600">
              Connect with nearby colleagues and share rides when parking is
              full.
            </p>
          </div>

          <Tabs defaultValue="matches" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2 mb-8">
              <TabsTrigger data-testid="tab-matches" value="matches">
                Find Matches
              </TabsTrigger>
              <TabsTrigger data-testid="tab-requests" value="requests">
                My Requests ({requests.incoming.length + requests.sent.length})
              </TabsTrigger>
            </TabsList>

            {/* Matches Tab */}
            <TabsContent value="matches">
              <Label
                htmlFor="shift"
                className="text-gray-700 font-medium text-lg mb-3 block"
              >
                Select Shift
              </Label>
              <Select
                value={requestData.timeShift}
                onValueChange={(value) =>
                  setRequestData({ ...requestData, timeShift: value })
                }
              >
                <SelectTrigger
                  data-testid="select-shift"
                  className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-14 text-base"
                >
                  <SelectValue placeholder="Choose your shift" />
                </SelectTrigger>
                <SelectContent>
                  {timeShifts.map((shift) => (
                    <SelectItem key={shift._id} value={shift._id}>
                      {shift.start_time} - {shift.end_time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="my-6" />
              {matches.length === 0 ? (
                <Card className="p-12 text-center">
                  <Users className="mx-auto mb-4 text-gray-400" size={48} />
                  <p className="text-gray-600 text-lg">
                    No nearby colleagues found
                  </p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {matches.map((match) => (
                    <Card
                      key={match._id}
                      data-testid={`match-${match._id}`}
                      className="p-6 border border-gray-100 hover:shadow-xl transition-all"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-14 h-14 bg-[#1A3C34] rounded-full flex items-center justify-center">
                          <span className="text-white text-xl font-bold">
                            {match.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-600">
                          <MapPin size={16} />
                          <span className="text-sm">
                            {Math.round(match.distance / 1000)} km
                          </span>
                        </div>
                      </div>

                      <h3 className="text-xl font-bold text-[#1A3C34] mb-2">
                        {match.name}
                      </h3>

                      <div className="space-y-2 mb-4">
                        <p className="text-gray-600 text-sm">
                          <span className="font-medium">Organization:</span>{" "}
                          {match.organization}
                        </p>
                        <p className="text-gray-600 text-sm">
                          <span className="font-medium">Address:</span>{" "}
                          {match.address}
                        </p>
                      </div>

                      {showRequestDialog && selectedUser === match._id ? (
                        <div className="space-y-3">
                          <Textarea
                            data-testid={`request-message-${match._id}`}
                            placeholder="Hi! Can we carpool on this date?"
                            value={requestData.message}
                            onChange={(e) =>
                              setRequestData({
                                ...requestData,
                                message: e.target.value,
                              })
                            }
                            className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg"
                            rows={3}
                          />
                          <div className="flex gap-2">
                            <Button
                              data-testid={`send-request-${match._id}`}
                              onClick={() => handleSendRequest(match._id)}
                              className="flex-1 bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-lg"
                            >
                              Send
                            </Button>
                            <Button
                              data-testid={`cancel-request-${match._id}`}
                              onClick={() => {
                                setShowRequestDialog(false);
                                setSelectedUser(null);
                                setRequestData({ date: "", message: "" });
                              }}
                              variant="outline"
                              className="flex-1 rounded-lg"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          data-testid={`request-carpool-${match._id}`}
                          onClick={() => {
                            setShowRequestDialog(true);
                            setSelectedUser(match._id);
                          }}
                          className="w-full bg-[#FF6B35] hover:bg-[#E55A28] text-white rounded-lg"
                        >
                          <Send className="mr-2" size={16} />
                          Request Carpool
                        </Button>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Requests Tab */}
            <TabsContent value="requests">
              <div className="space-y-8">
                {/* Incoming Requests */}
                <div>
                  <h2 className="text-2xl font-bold text-[#1A3C34] mb-4">
                    Incoming Requests
                  </h2>

                  {acceptedIncoming.length > 0 && (
                    <button
                      type="button"
                      data-testid="open-group-trip"
                      onClick={() => setShowGroupTrip(true)}
                      className="w-full flex items-center gap-3 bg-[#F5F2EB] border border-[#E4DCC9] rounded-xl p-4 mb-6 text-left hover:bg-[#efe9da] transition-colors"
                    >
                      <span className="w-10 h-10 rounded-full bg-[#1A3C34] flex items-center justify-center text-white shrink-0">
                        <Map size={20} />
                      </span>
                      <span className="flex-1">
                        <span className="block font-bold text-[#1A3C34]">Plan your group trip</span>
                        <span className="block text-sm text-gray-600">
                          {acceptedIncoming.length} passenger{acceptedIncoming.length === 1 ? "" : "s"} · optimized route &amp; one-tap start
                        </span>
                      </span>
                      <ChevronRight size={20} className="text-[#1A3C34]" />
                    </button>
                  )}

                  {requests.incoming.length === 0 ? (
                    <Card className="p-8 text-center">
                      <p className="text-gray-600">No incoming requests</p>
                    </Card>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                      {requests.incoming.map((req) => (
                        <Card
                          key={req._id}
                          data-testid={`incoming-request-${req._id}`}
                          className="p-6 border border-gray-100"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="text-xl font-bold text-[#1A3C34]">
                                {req.requester?.name}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {req.requester?.email}
                              </p>
                            </div>
                            <div
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                req.status === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : req.status === "accepted"
                                    ? "bg-green-100 text-green-800"
                                    : req.status === "started"
                                      ? "bg-blue-100 text-blue-800"
                                      : req.status === "arrived"
                                        ? "bg-purple-100 text-purple-800"
                                        : req.status === "picked_up"
                                          ? "bg-indigo-100 text-indigo-800"
                                          : req.status === "completed"
                                            ? "bg-gray-100 text-gray-800"
                                            : "bg-red-100 text-red-800"
                              }`}
                            >
                              {req.status === "started" ? "On the way" : 
                               req.status === "arrived" ? "At Pickup" :
                               req.status === "picked_up" ? "In Transit" :
                               req.status}
                            </div>
                          </div>

                          <div className="mb-4">
                            <p className="text-gray-600 mb-2">
                              <span className="font-medium">Date:</span>{" "}
                              {req.date}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-medium">Message:</span>{" "}
                              {req.message}
                            </p>
                          </div>

                          {req.detour && (
                            <div className="mb-4 flex items-center gap-2 text-sm text-gray-700">
                              <Navigation size={14} className="text-[#FF6B35]" />
                              <span className="font-medium">Detour:</span>
                              <span>
                                +{req.detour.minutes} min, {req.detour.distance_km} km
                              </span>
                            </div>
                          )}

                          <Button
                            data-testid={`show-distance-${req._id}`}
                            onClick={() => {
                              setSelectedRouteRequest(req);
                              setShowMapDialog(true);
                            }}
                            variant="outline"
                            className="w-full mb-3 border-[#1A3C34] text-[#1A3C34] hover:bg-[#F5F2EB] rounded-lg"
                          >
                            <Navigation className="mr-2" size={16} />
                            Show Distance
                          </Button>

                          {/* Driver Tracking Button - for accepted/started/arrived/picked_up status */}
                          {["accepted", "started", "arrived", "picked_up"].includes(req.status) && (
                            <Button
                              data-testid={`driver-tracking-${req._id}`}
                              onClick={() => navigate(`/carpool/drive/${req._id}`)}
                              className="w-full mb-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                            >
                              {req.status === "accepted" ? (
                                <>
                                  <Play className="mr-2" size={16} />
                                  Start Ride
                                </>
                              ) : (
                                <>
                                  <Car className="mr-2" size={16} />
                                  Continue Ride
                                </>
                              )}
                            </Button>
                          )}

                          {/* Completed Status */}
                          {req.status === "completed" && (
                            <div className="flex items-center justify-center gap-2 py-3 bg-gray-50 rounded-lg">
                              <CheckCircle className="text-green-600" size={20} />
                              <span className="text-green-700 font-medium">Ride Completed</span>
                            </div>
                          )}

                          {req.status === "pending" && (
                            <div className="flex gap-2">
                              <Button
                                data-testid={`accept-request-${req._id}`}
                                onClick={() =>
                                  handleUpdateRequest(req._id, "accepted")
                                }
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                              >
                                <Check className="mr-2" size={16} />
                                Accept
                              </Button>
                              <Button
                                data-testid={`reject-request-${req._id}`}
                                onClick={() =>
                                  handleUpdateRequest(req._id, "rejected")
                                }
                                variant="outline"
                                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <X className="mr-2" size={16} />
                                Reject
                              </Button>
                            </div>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sent Requests */}
                <div>
                  <h2 className="text-2xl font-bold text-[#1A3C34] mb-4">
                    Sent Requests
                  </h2>
                  {requests.sent.length === 0 ? (
                    <Card className="p-8 text-center">
                      <p className="text-gray-600">No sent requests</p>
                    </Card>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                      {requests.sent.map((req) => (
                        <Card
                          key={req._id}
                          data-testid={`sent-request-${req._id}`}
                          className="p-6 border border-gray-100"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="text-xl font-bold text-[#1A3C34]">
                                {req.recipient?.name}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {req.recipient?.email}
                              </p>
                            </div>
                            <div
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                req.status === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : req.status === "accepted"
                                    ? "bg-green-100 text-green-800"
                                    : req.status === "started"
                                      ? "bg-blue-100 text-blue-800"
                                      : req.status === "arrived"
                                        ? "bg-purple-100 text-purple-800"
                                        : req.status === "picked_up"
                                          ? "bg-indigo-100 text-indigo-800"
                                          : req.status === "completed"
                                            ? "bg-gray-100 text-gray-800"
                                            : "bg-red-100 text-red-800"
                              }`}
                            >
                              {req.status === "started" ? "Driver on the way" : 
                               req.status === "arrived" ? "Driver Arrived" :
                               req.status === "picked_up" ? "In Transit" :
                               req.status}
                            </div>
                          </div>

                          <div className="mb-4">
                            <p className="text-gray-600 mb-2">
                              <span className="font-medium">Date:</span>{" "}
                              {req.date}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-medium">Message:</span>{" "}
                              {req.message}
                            </p>
                          </div>

                          {/* Track Driver Button - for started/arrived/picked_up status */}
                          {["started", "arrived", "picked_up"].includes(req.status) && (
                            <Button
                              data-testid={`track-driver-${req._id}`}
                              onClick={() => navigate(`/carpool/track/${req._id}`)}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                            >
                              <Eye className="mr-2" size={16} />
                              {req.status === "arrived" ? "Driver Arrived - View Location" : "Track Driver Live"}
                            </Button>
                          )}

                          {/* Waiting for driver status */}
                          {req.status === "accepted" && (
                            <div className="flex items-center justify-center gap-2 py-3 bg-green-50 rounded-lg">
                              <Check className="text-green-600" size={20} />
                              <span className="text-green-700 font-medium">Request Accepted - Waiting for driver to start</span>
                            </div>
                          )}

                          {/* Completed Status */}
                          {req.status === "completed" && (
                            <div className="flex items-center justify-center gap-2 py-3 bg-gray-50 rounded-lg">
                              <CheckCircle className="text-green-600" size={20} />
                              <span className="text-green-700 font-medium">Ride Completed</span>
                            </div>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Group Trip Dialog */}
      <Dialog open={showGroupTrip} onOpenChange={setShowGroupTrip}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0 gap-0 overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>Group Trip</DialogTitle>
          </DialogHeader>
          {showGroupTrip && (
            <GroupTripPanel
              date={groupTripDate}
              timeWindow={acceptedIncoming[0]?.time_window}
              onClose={() => setShowGroupTrip(false)}
              onTripStarted={() => {
                fetchData();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Map Dialog for showing directions */}
      <Dialog open={showMapDialog} onOpenChange={setShowMapDialog}>
        <DialogContent className="max-w-4xl h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-[#1A3C34]">
              Route to {selectedRouteRequest?.requester?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedRouteRequest?.requester && (
            <div className="flex-1 h-full">
              <div className="mb-4 p-4 bg-[#F5F2EB] rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">From:</span> {selectedRouteRequest.requester.name}'s location
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">To:</span> Your location
                </p>
              </div>
              <iframe
                title="Route directions map"
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: "12px" }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/directions?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8"}&origin=${selectedRouteRequest.requester.latitude},${selectedRouteRequest.requester.longitude}&destination=${selectedRouteRequest.recipient?.latitude || user?.location?.coordinates?.[1]},${selectedRouteRequest.recipient?.longitude || user?.location?.coordinates?.[0]}&mode=driving&zoom=17`}
              ></iframe>
              <div className="mt-4 flex gap-2">
                <Button
                  onClick={() => {
                    const url = `https://www.google.com/maps/dir/?api=1&origin=${selectedRouteRequest.requester.latitude},${selectedRouteRequest.requester.longitude}&destination=${selectedRouteRequest.recipient?.latitude || user?.location?.coordinates?.[1]},${selectedRouteRequest.recipient?.longitude || user?.location?.coordinates?.[0]}&travelmode=driving&zoom=17`;
                    window.open(url, "_blank");
                  }}
                  className="flex-1 bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-lg"
                >
                  <Navigation className="mr-2" size={16} />
                  Open in Google Maps
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowMapDialog(false)}
                  className="flex-1 rounded-lg"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CarpoolPage;